import { AlertTriangle, CheckCircle2, Circle, Clock3, Loader2 } from 'lucide-react'
import { Typography } from '~/components/common/Typography'
import { TaskStatuses } from '~/constants/task-status'
import { ProgressMessage } from '~/hooks/task/useTaskProgressMessages'
import { agentTaskStatusFromJSON } from '~/protos/agents'
import { cm } from '~/utils'

interface LandingTaskProgressTimelineProps {
  emptyDescription: string
  emptyTitle: string
  isRunning: boolean
  progressMessages: ProgressMessage[]
}

function progressTitle(message: ProgressMessage) {
  return message.title || message.checkpointMsg || 'Working'
}

function formatProgressTime(createdTsMs?: number) {
  if (!createdTsMs) return ''
  return new Date(createdTsMs).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit'
  })
}

function progressTone(message: ProgressMessage, isLast: boolean, isRunning: boolean) {
  if (isLast && isRunning) {
    return {
      icon: Loader2,
      iconClassName: 'animate-spin',
      ringClassName:
        'border-tokens-status-link-2/20 bg-tokens-status-link-base-2 text-tokens-status-link-3',
      label: 'Running'
    }
  }

  const status = message.taskStatus ? agentTaskStatusFromJSON(message.taskStatus) : undefined
  const group = status ? TaskStatuses[status]?.group : undefined
  if (group === 'ERRORED') {
    return {
      icon: AlertTriangle,
      iconClassName: '',
      ringClassName:
        'border-tokens-status-error-30 bg-tokens-status-error-10 text-tokens-status-error-text',
      label: 'Failed'
    }
  }

  return {
    icon: CheckCircle2,
    iconClassName: '',
    ringClassName:
      'border-tokens-status-success-2 bg-tokens-status-success-3 text-tokens-status-success-1',
    label: 'Done'
  }
}

function todoStatusClass(status: string) {
  if (status === 'TODO_COMPLETED') return 'text-tokens-status-success-1'
  if (status === 'TODO_IN_PROGRESS') return 'text-tokens-status-link-3'
  return 'text-tokens-neutral-text-tertiary'
}

export default function LandingTaskProgressTimeline({
  emptyDescription,
  emptyTitle,
  isRunning,
  progressMessages
}: LandingTaskProgressTimelineProps) {
  if (progressMessages.length === 0) {
    return (
      <div className='flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-tokens-neutral-text-tertiary'>
        {isRunning ? <Loader2 className='h-5 w-5 animate-spin' /> : <Clock3 className='h-5 w-5' />}
        <Typography fontFamily='geist' fontSize='bodyMd' fontWeight='semibold'>
          {emptyTitle}
        </Typography>
        <div className='max-w-xs text-sm'>{emptyDescription}</div>
      </div>
    )
  }

  const recentMessages = progressMessages.slice(-12)
  const lastMessage = recentMessages[recentMessages.length - 1]
  const lastUpdated = formatProgressTime(lastMessage?.createdTsMs)

  return (
    <div className='p-4'>
      <div className='mb-4 rounded-2xl border border-tokens-neutral-border-2 bg-tokens-neutral-bg-grey-1 px-3 py-2'>
        <div className='flex items-center justify-between gap-3'>
          <Typography
            fontFamily='geist'
            fontSize='bodySm'
            fontWeight='semibold'
            className='text-tokens-neutral-text-primary'
          >
            Progress timeline
          </Typography>
          {lastUpdated && (
            <span className='shrink-0 text-xs text-tokens-neutral-text-tertiary'>
              Last update {lastUpdated}
            </span>
          )}
        </div>
      </div>

      <div className='space-y-3'>
        {recentMessages.map((message, index) => {
          const isLast = index === recentMessages.length - 1
          const tone = progressTone(message, isLast, isRunning)
          const Icon = tone.icon
          const time = formatProgressTime(message.createdTsMs)

          return (
            <div
              key={`${message.msgHash}-${index}`}
              className='grid grid-cols-[24px_minmax(0,1fr)] gap-3'
            >
              <div className='flex flex-col items-center'>
                <div
                  className={cm(
                    'flex h-6 w-6 items-center justify-center rounded-full border',
                    tone.ringClassName
                  )}
                  title={tone.label}
                >
                  <Icon className={cm('h-3.5 w-3.5', tone.iconClassName)} />
                </div>
                {!isLast && <div className='mt-1 w-px flex-1 bg-tokens-neutral-border-2' />}
              </div>
              <div className='min-w-0 pb-2'>
                <div className='flex min-w-0 items-start justify-between gap-2'>
                  <Typography
                    fontFamily='geist'
                    fontSize='bodySm'
                    fontWeight='semibold'
                    className='min-w-0 break-words text-tokens-neutral-text-primary'
                  >
                    {progressTitle(message)}
                  </Typography>
                  {time && (
                    <span className='shrink-0 text-[11px] text-tokens-neutral-text-tertiary'>
                      {time}
                    </span>
                  )}
                </div>

                {message.todoList && message.todoList.length > 0 && (
                  <div className='mt-2 space-y-1 rounded-xl border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white p-2'>
                    {message.todoList.slice(0, 5).map((todo, todoIndex) => (
                      <div
                        key={`${todo.todo}-${todoIndex}`}
                        className='flex items-start gap-2 text-xs text-tokens-neutral-text-secondary'
                      >
                        <Circle
                          className={cm('mt-0.5 h-3 w-3 shrink-0', todoStatusClass(todo.status))}
                          fill={todo.status === 'TODO_COMPLETED' ? 'currentColor' : 'none'}
                        />
                        <span className='break-words'>{todo.todo}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
